import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import { Skeleton } from "@/components/ui/skeleton";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { INSURANCES, SPECIALTIES, searchProvidersWithMeta, countProvidersWithFilters, type Provider } from "@/data/heallexa";
import { ProviderCard } from "@/components/search/ProviderCard";
import { citiesInState, US_CITIES_VALID } from "@/data/us-cities";
import {
  SEO_INSURANCE_IDS,
  specialtySlug,
  stateFromNameSlug,
  stateNameSlug,
  toSlug,
} from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";
const PAGE_SIZE = 20;

function CityPageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Skeleton className="h-4 w-48 mb-6" />
        <div className="mb-10">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-12 w-2/3 mb-4" />
          <Skeleton className="h-4 w-full max-w-lg mb-6" />
          <div className="flex gap-3">
            <Skeleton className="h-10 w-44" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card-surface-flat rounded-md p-4 flex gap-4">
              <Skeleton className="h-16 w-16 shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </main>
      <FooterSection />
    </div>
  );
}

export const Route = createFileRoute("/locations/$state/$city")({
  validateSearch: (s: Record<string, unknown>) => ({
    page: typeof s.page === "number" ? s.page : (typeof s.page === "string" ? Number(s.page) || undefined : undefined),
  }),
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ params, deps }) => {
    await setSeoPageCache();
    const state = stateFromNameSlug(params.state);
    if (!state) throw notFound();
    const citySlug = params.city.toLowerCase();
    const city = US_CITIES_VALID.find(
      (c) => c.state === state.code && toSlug(c.name) === citySlug,
    );
    if (!city) throw notFound();
    const page = Math.max(1, Number(deps.page) || 1);
    const offset = (page - 1) * PAGE_SIZE;
    let providers: Provider[] = [];
    let hasMore = false;
    let total = 0;
    const loc = `${city.name}, ${state.code}`;
    try {
      const [{ results }, countRes] = await Promise.all([
        searchProvidersWithMeta({ location: loc, limit: PAGE_SIZE + 1, offset }),
        countProvidersWithFilters({ location: loc }).catch(() => ({ count: 0, capped: false })),
      ]);
      hasMore = results.length > PAGE_SIZE;
      providers = results.slice(0, PAGE_SIZE);
      total = countRes.count;
    } catch {
      providers = [];
    }
    return { state, city, providers, page, hasMore, total };
  },
  pendingMs: 100,
  pendingMinMs: 300,
  pendingComponent: CityPageSkeleton,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { state, city } = loaderData;
    const title = `Healthcare Providers in ${city.name}, ${state.code} | Heallexa`;
    const description = `Find healthcare providers in ${city.name}, ${state.code}. Search doctors, dentists, therapists, chiropractors, specialists, and clinics near you.`;
    const stateSlug = stateNameSlug(state.name);
    const url = `${SITE}/locations/${stateSlug}/${toSlug(city.name)}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
            { name: state.name, url: `/locations/${stateSlug}` },
            { name: city.name, url: `/locations/${stateSlug}/${toSlug(city.name)}` },
          ]),
        ),
        jsonLdScript(
          faqPageSchema([
            {
              q: `How do I find a doctor in ${city.name}, ${state.code}?`,
              a: `Use Heallexa to browse ${city.name} providers by specialty and insurance, then contact the office directly.`,
            },
            {
              q: `Which insurance plans do ${city.name} providers accept?`,
              a: `Most ${city.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare.`,
            },
            {
              q: `Is Heallexa free for patients?`,
              a: `Yes. Search, browse, and contact providers in ${city.name} for free, no signup required.`,
            },
          ]),
        ),
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-2xl font-semibold mb-2">City not found</h1>
        <Link to="/locations" className="text-primary underline">
          Browse all locations
        </Link>
      </div>
    </div>
  ),
});

function CityPage() {
  const { state, city, providers, page, hasMore, total } = Route.useLoaderData();
  const navigate = useNavigate({ from: "/locations/$state/$city" });
  const stateSlug = stateNameSlug(state.name);
  const cs = `${toSlug(city.name)}-${state.code.toLowerCase()}`;
  const topSpecialties = SPECIALTIES.slice(0, 16);
  const nearby = citiesInState(state.code)
    .filter((c) => c.name !== city.name)
    .slice(0, 12);

  const knownPages = total > 0 ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 0;
  const fallbackPages = page + (hasMore ? 1 : 0);
  const totalPages = Math.max(knownPages, fallbackPages, 1);

  const setPage = (p: number) =>
    navigate({
      search: (prev: any) => ({ ...prev, page: p === 1 ? undefined : p }),
      replace: false,
    });

  const start = (page - 1) * PAGE_SIZE + 1;
  const end = (page - 1) * PAGE_SIZE + providers.length;
  const totalLabel = total > 0
    ? `${total.toLocaleString()} result${total === 1 ? "" : "s"}`
    : `${end}${hasMore ? "+" : ""} results`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Breadcrumbs
          className="mb-6"
          items={[
            { name: "Home", href: "/" },
            { name: "Locations", href: "/locations" },
            { name: state.name, href: `/locations/${stateSlug}` },
            { name: city.name },
          ]}
        />
        <Reveal from="up">
          <header className="mb-12">
            <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">
              {city.name}, {state.code}
            </p>
            <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
              Healthcare Providers in {city.name}, {state.code}
            </h1>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
              Find healthcare providers in {city.name}, {state.code}. Search doctors, dentists,
              therapists, chiropractors, specialists, and clinics near you.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/search"
                search={{ location: `${city.name}, ${state.code}` }}
                className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
              >
                Search providers in {city.name}
              </Link>
              <Link
                to="/list-your-practice"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium border border-border bg-background text-foreground hover:bg-muted transition-colors rounded-md"
              >
                List your practice
              </Link>
            </div>
          </header>
        </Reveal>

        {providers.length > 0 ? (
          <section className="mb-16">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
              <div>
                <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
                  Local directory
                </p>
                <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
                  Providers in {city.name}
                </h2>
              </div>
              <span className="text-[13px] text-muted-foreground tabular-nums">
                Showing{" "}
                <span className="font-medium text-foreground">{start}–{end}</span>{" "}
                of <span className="font-medium text-foreground">{totalLabel}</span>
              </span>
            </div>

            <ul className="grid gap-5">
              {providers.map((p, idx) => (
                <Reveal key={p.id} as="li" from="up" delay={Math.min(idx, 8) * 70}>
                  <ProviderCard p={p} />
                </Reveal>
              ))}
            </ul>

            <nav aria-label="Pagination" className="flex items-center justify-center gap-3 pt-6">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-[13px] text-muted-foreground tabular-nums">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={!hasMore && page >= totalPages}
                className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
                aria-label="Next page"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </nav>
          </section>
        ) : (
          <div className="mb-16 card-surface p-10 text-center text-[14px] text-muted-foreground rounded-md">
            No providers listed yet for {city.name}.{" "}
            <Link to="/search" search={{ location: `${city.name}, ${state.code}` }} className="text-primary underline">
              Try the search page
            </Link>.
          </div>
        )}

        <Reveal from="up" delay={60}>
          <SeoLinkList
            heading={`Top specialties in ${city.name}`}
            links={topSpecialties.map((s) => ({
              label: `${s} in ${city.name}`,
              href: `/specialty/${specialtySlug(s)}/${cs}`,
            }))}
            columns={3}
            className="mb-10"
          />
        </Reveal>

        <Reveal from="up" delay={60}>
          <SeoLinkList
            heading={`Insurance accepted in ${city.name}`}
            links={SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id))
              .filter(Boolean)
              .map((ins) => ({
                label: `Doctors in ${city.name} accepting ${ins!.short}`,
                href: `/insurance/${ins!.id}/${cs}`,
              }))}
            columns={3}
            className="mb-10"
          />
        </Reveal>

        {nearby.length > 0 && (
          <Reveal from="up" delay={60}>
            <SeoLinkList
              heading={`Nearby cities in ${state.name}`}
              links={nearby.map((c) => ({
                label: c.name,
                href: `/locations/${stateSlug}/${toSlug(c.name)}`,
              }))}
              columns={3}
              icon={MapPin}
              className="mb-10"
            />
          </Reveal>
        )}

        <SeoFaq
          className="mb-8"
          items={[
            {
              q: `How do I find a doctor in ${city.name}?`,
              a: `Use Heallexa to filter ${city.name} providers by specialty and insurance, then contact the office directly.`,
            },
            {
              q: `Are these ${city.name} doctors accepting new patients?`,
              a: `Most listings indicate whether the provider is accepting new patients. Always confirm with the office.`,
            },
            {
              q: `Which insurance plans do ${city.name} doctors accept?`,
              a: `Most accept major plans like BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare. Use the filter above to narrow down.`,
            },
            {
              q: `Is Heallexa free for patients?`,
              a: `Yes. Search, browse, and contact ${city.name} providers for free, no signup required.`,
            },
          ]}
        />
      </main>
      <FooterSection />
    </div>
  );
}
